/*
 * Cloudflare Turnstile widget (brand signup bot check; design §2.6 + ruling 2:
 * Turnstile plus a honeypot). The script is loaded on demand, once per page,
 * in explicit-render mode, and the widget renders into this component's box.
 * my-api verifies the token server-side (403 turnstile_failed on a bad one).
 *
 * `resetKey` re-renders a fresh challenge (a used or rejected token can't be
 * re-submitted). No CSP lives in this repo today (index.html has no policy
 * meta); a future policy must allow https://challenges.cloudflare.com in
 * script-src and frame-src.
 */
import { useEffect, useRef } from 'react'

export const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove?: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let loading: Promise<TurnstileApi> | null = null

/** Test seam. */
export function resetTurnstileLoaderForTests(): void {
  loading = null
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (loading) return loading
  loading = new Promise<TurnstileApi>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = TURNSTILE_SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile missing')))
    s.onerror = () => {
      loading = null
      reject(new Error('turnstile script failed'))
    }
    document.head.appendChild(s)
  })
  return loading
}

export function Turnstile({ siteKey, onToken, onError, resetKey = 0, label }: {
  siteKey: string
  /** A fresh token, or null when it expired / errored. */
  onToken: (token: string | null) => void
  onError?: () => void
  resetKey?: number
  label: string
}) {
  const box = useRef<HTMLDivElement | null>(null)
  // Callbacks via refs so a parent re-render never re-mounts the widget.
  const tokenCb = useRef(onToken)
  const errorCb = useRef(onError)
  tokenCb.current = onToken
  errorCb.current = onError

  useEffect(() => {
    let cancelled = false
    let widgetId: string | null = null
    tokenCb.current(null)
    loadTurnstile()
      .then((api) => {
        if (cancelled || !box.current) return
        box.current.innerHTML = ''
        widgetId = api.render(box.current, {
          sitekey: siteKey,
          callback: (t: string) => tokenCb.current(t),
          'expired-callback': () => tokenCb.current(null),
          'error-callback': () => {
            tokenCb.current(null)
            errorCb.current?.()
          },
        })
      })
      .catch(() => {
        if (!cancelled) errorCb.current?.()
      })
    return () => {
      cancelled = true
      if (widgetId && window.turnstile?.remove) {
        try {
          window.turnstile.remove(widgetId)
        } catch {
          /* already gone */
        }
      }
    }
  }, [siteKey, resetKey])

  return <div ref={box} data-testid="join-turnstile" role="group" aria-label={label} style={{ minHeight: 65 }} />
}
